import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
} from "@heroicons/react/solid";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageGallery from "react-image-gallery";
import QuranPagesService from "../../services/quranPagesService";
import "./quran-viewer.scss";

type QuranViewerProps = {
  startPage?: number;
  onClose?: (key: number | undefined) => void;
};

export default function QuranViewer(props: QuranViewerProps) {
  const { startPage = 1, onClose = () => {} } = props;
  const [images, setImages] = useState<any[]>([]);
  const imageGalleryRef = useRef<ImageGallery>(null);
  const [page, setPage] = useState(startPage - 1);
  const { t } = useTranslation("", {
    keyPrefix: "quraanViewer",
  });

  useEffect(() => {
    populateQuranPages();
  }, []);

  useEffect(() => {
    if (imageGalleryRef.current) {
      imageGalleryRef.current?.slideToIndex(page);
    }
  }, [page]);

  const populateQuranPages = () => {
    let pagesUrls = QuranPagesService.getAllQuranPages();
    let pagesImages = pagesUrls.map(urlToImageObject);
    setImages(pagesImages);
  };

  function urlToImageObject(url: string) {
    return {
      original: url,
    };
  }

  const goToNextPage = () => {
    let nextPage = page + 1;
    if (nextPage >= images.length) {
      nextPage = 0;
    }
    setPage(nextPage);
  };

  const goToPreviousPage = () => {
    let previousPage = page - 1;
    if (previousPage < 0) {
      previousPage = images.length - 1;
    }
    setPage(previousPage);
  };

  return (
    <div className="flex h-full flex-col bg-white divide-y divide-gray-200">
      <div className="p-4 shadow-sm">
        <div className="flex gap-2 items-center justify-between">
          <div className="text-sm font-medium text-gray-700">{t("pages")}</div>
          <div className="ms-3 flex h-7 items-center">
            <button type="button" onClick={() => onClose(page + 1)}>
              <ChevronLeftIcon className="h-5 w-5 -rotate-90 sm:rtl:rotate-0 sm:ltr:rotate-180" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll quraan-viewer">
        <div className="relative p-5">
          <ImageGallery
            ref={imageGalleryRef}
            items={images}
            startIndex={startPage - 1}
            isRTL={true}
            lazyLoad={true}
            showThumbnails={false}
            infinite={true}
            autoPlay={false}
            showPlayButton={false}
            showFullscreenButton={false}
            showNav={false}
            slideDuration={200}
            onTouchEnd={(e) => {
              setPage(imageGalleryRef?.current?.getCurrentIndex() ?? 1);
            }}
          />
        </div>
      </div>
      <div
        className="flex items-center justify-between flex-shrink-0 px-2 py-0.5 text-sm font-medium text-gray-700"
        dir="rtl"
      >
        <button
          className="flex items-center gap-2 p-[14px]"
          onClick={goToPreviousPage}
        >
          <ArrowRightIcon className="h-5 w-5" />
          <span>{t("prev")}</span>
        </button>
        <div>{page + 1}</div>
        <button
          className="flex items-center gap-2 p-[14px]"
          onClick={goToNextPage}
        >
          <span>{t("next")}</span>
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
